import { Preferences } from '@capacitor/preferences';
import { Capacitor, registerPlugin } from '@capacitor/core';

const ACCOUNT_PASSWORDS_KEY = 'savedAccountPasswords';
const OBFUSCATION_SEED = 'clubroots-switch-v1';
const SECURE_KEY_PREFIX = 'quick-switch:';

const NativeBiometric = registerPlugin<any>('NativeBiometric');
const SecureStoragePlugin = registerPlugin<any>('SecureStoragePlugin');

const toBase64 = (value: string): string => {
  if (typeof btoa === 'function') return btoa(value);
  return Buffer.from(value, 'utf-8').toString('base64');
};

const fromBase64 = (value: string): string => {
  if (typeof atob === 'function') return atob(value);
  return Buffer.from(value, 'base64').toString('utf-8');
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();
const toSecureKey = (email: string): string => `${SECURE_KEY_PREFIX}${normalizeEmail(email)}`;

const obfuscate = (plainText: string, email: string): string => {
  const key = `${OBFUSCATION_SEED}:${normalizeEmail(email)}`;
  let transformed = '';
  for (let i = 0; i < plainText.length; i++) {
    const keyCode = key.charCodeAt(i % key.length);
    transformed += String.fromCharCode(plainText.charCodeAt(i) ^ keyCode);
  }
  return toBase64(transformed);
};

const deobfuscate = (cipherText: string, email: string): string => {
  const decoded = fromBase64(cipherText);
  const key = `${OBFUSCATION_SEED}:${normalizeEmail(email)}`;
  let plain = '';
  for (let i = 0; i < decoded.length; i++) {
    const keyCode = key.charCodeAt(i % key.length);
    plain += String.fromCharCode(decoded.charCodeAt(i) ^ keyCode);
  }
  return plain;
};

const getStoredMap = async (): Promise<Record<string, string>> => {
  try {
    const { value } = await Preferences.get({ key: ACCOUNT_PASSWORDS_KEY });
    if (!value) return {};
    const parsed = JSON.parse(value) as Record<string, string>;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
};

const saveSecure = async (email: string, password: string): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await SecureStoragePlugin.set({ key: toSecureKey(email), value: password });
    return true;
  } catch {
    return false;
  }
};

const getSecure = async (email: string): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const resp = await SecureStoragePlugin.get({ key: toSecureKey(email) });
    return resp?.value ?? null;
  } catch {
    return null;
  }
};

const removeSecure = async (email: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SecureStoragePlugin.remove({ key: toSecureKey(email) });
  } catch {
    // no-op
  }
};

const verifyBiometricForQuickSwitch = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return true;
  try {
    const availability = await NativeBiometric.isAvailable();
    const available = availability?.isAvailable === true;
    if (!available) return true;

    await NativeBiometric.verifyIdentity({
      reason: 'Authenticate to switch account',
      title: 'Quick Account Switch',
      subtitle: 'Confirm your identity',
      description: 'Use your biometric unlock to continue',
    });
    return true;
  } catch {
    return false;
  }
};

export const saveEmailPassword = async (email: string, password: string): Promise<void> => {
  if (!email?.trim() || !password) return;
  const savedSecurely = await saveSecure(email, password);
  if (savedSecurely) return;
  const map = await getStoredMap();
  map[normalizeEmail(email)] = obfuscate(password, email);
  await Preferences.set({ key: ACCOUNT_PASSWORDS_KEY, value: JSON.stringify(map) });
};

export const getEmailPassword = async (
  email: string,
  options?: { requireBiometric?: boolean }
): Promise<string | null> => {
  if (!email?.trim()) return null;

  if (options?.requireBiometric) {
    const verified = await verifyBiometricForQuickSwitch();
    if (!verified) return null;
  }

  const secureValue = await getSecure(email);
  if (secureValue) return secureValue;

  const map = await getStoredMap();
  const encrypted = map[normalizeEmail(email)];
  if (!encrypted) return null;
  try {
    return deobfuscate(encrypted, email);
  } catch {
    return null;
  }
};

export const hasEmailPassword = async (email: string): Promise<boolean> => {
  if (!email?.trim()) return false;
  const secure = await getSecure(email);
  if (secure) return true;
  const map = await getStoredMap();
  return !!map[normalizeEmail(email)];
};

export const removeEmailPassword = async (email: string): Promise<void> => {
  if (!email?.trim()) return;
  await removeSecure(email);
  const map = await getStoredMap();
  delete map[normalizeEmail(email)];
  await Preferences.set({ key: ACCOUNT_PASSWORDS_KEY, value: JSON.stringify(map) });
};

export const canUseBiometricQuickSwitch = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const availability = await NativeBiometric.isAvailable();
    return availability?.isAvailable === true;
  } catch {
    return false;
  }
};
